import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '@environments/environment';
import { SCOPES } from '../authorization/constants/scopes.constant';
import { MsalService } from '@azure/msal-angular';
import { SESSION_STORAGE_KEYS } from '../session_storage/constants/session-storage-keys.constant';
import { isSpecificTenant, resolveAccountForTenant } from './account-selection.util';

export let tenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID) ?? 'organizations';

export let authConfig = {
  auth: {
    clientId: environment.clientId,
    authority: `https://login.microsoftonline.com/${tenantId}/`,
    redirectUri: '/',
    postLogoutRedirectUri: '/',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: true, // Aktiviert für bessere Resilience bei LocalStorage-Löschung
  },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback,
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

export function loggerCallback(logLevel: LogLevel, message: string) {
  // fürs Bessere loggin in der Konsole einschalten
  //console.log(message);
}

export function setMsalTenantId(newTenantId: string) {
  tenantId = newTenantId;
}

export function getMsalTenantId() {
  return tenantId;
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(authConfig);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [SCOPES.USER_IMPERSONATION],
    },
    loginFailedRoute: './login-failed',
  };
}

export function MSALInterceptorConfigFactory(msalService: MsalService): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      [environment.baseUrl + 'tenants/connected', ['https://management.azure.com/user_impersonation']],
      [environment.baseUrl + 'management/onboarding/confirm', ['https://management.azure.com/user_impersonation']],
      [environment.baseUrl + 'management/onboarding/finish', ['https://management.azure.com/user_impersonation']],
      [environment.baseUrl + 'management/onboarding/all', ['api://c08e2c98-a937-4642-837e-2105b3296830/api.scope']],
      [environment.baseUrl + 'management/onboarding/new', ['api://c08e2c98-a937-4642-837e-2105b3296830/api.scope']],
      [environment.baseUrl + 'management', ['api://c08e2c98-a937-4642-837e-2105b3296830/api.scope']],
      [environment.baseUrl + 'management/offboarding', ['api://c08e2c98-a937-4642-837e-2105b3296830/api.scope']],
      [environment.baseUrl + 'api', ['api://c08e2c98-a937-4642-837e-2105b3296830/api.scope']],
      ['https://management.azure.com/', ['https://management.azure.com/user_impersonation']],
      [
        'https://graph.microsoft.com/v1.0/applications',
        [
          'https://graph.microsoft.com/Application.ReadWrite.All',
          'https://graph.microsoft.com/Directory.ReadWrite.All',
        ],
      ],
      ['https://graph.microsoft.com/v1.0/servicePrincipals', ['https://graph.microsoft.com/Application.ReadWrite.All']],
      ['https://graph.microsoft.com/', ['https://graph.microsoft.com/user.read']],
    ]),

    authRequest: (_msalService, _httpReq, originalAuthRequest) => {
      const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID) ?? undefined;
      const allAccounts = _msalService.instance.getAllAccounts();
      const activeAccount = _msalService.instance.getActiveAccount();

      const resolvedAccount = resolveAccountForTenant(allAccounts, activeAccount, storedTenantId);

      if (!resolvedAccount) {
        if (isSpecificTenant(storedTenantId)) {
          throw new Error('No account available for the selected portal context');
        }
        // Before tenant selection, retain MSAL's normal interactive sign-in flow.
        return originalAuthRequest;
      }

      // Set active account if it changed
      if (
        !activeAccount ||
        activeAccount.tenantId !== resolvedAccount.tenantId ||
        activeAccount.homeAccountId !== resolvedAccount.homeAccountId
      ) {
        _msalService.instance.setActiveAccount(resolvedAccount);
      }

      // Determine authority - prefer resolved account's tenant, then stored, then organizations
      const effectiveTenantId = resolvedAccount.tenantId ?? storedTenantId ?? 'organizations';

      return {
        ...originalAuthRequest,
        account: resolvedAccount,
        authority: `https://login.microsoftonline.com/${effectiveTenantId}/`,
      };
    },
  };
}
